import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { BreadcrumbComponent } from '../../components/breadcrumb/breadcrumb.component';
import {
    DsBoxModule,
    DsButtonModule,
    DsCheckboxModule,
    DsFormFieldModule,
    DsIconModule,
    DsLabelModule,
    DsLoadingAreaConfiguration,
    DsLoadingAreaService,
    DsLoadingModule,
    DsMessageModule,
    DsModalModule,
    DsPopoverModule,
    DsProgressCircleModule,
    DsRadioGroupModule,
    DsSelectModule,
    DsTabsModule,
    DsToastModel,
    DsToastService,
    SelectListOption,
} from '@bmw-ds/components';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    CriteriaResponse,
    DealerModel,
    Version,
} from '../../models/criteria.model';
import { CriteriaStoreService } from '../../services/criteria-store.service';
import { CriteriaService } from '../../services/criteria.service';
import {
    BtoDownloadResponse,
    BtoQuote,
    BtoRequest,
    BtoUploadResponse,
} from '../../models/bto.model';
import { BtoQuoteService } from '../../services/bto.service';
import { BtoRowData } from '../../models/bto-row.model';
import { StockService } from '../../services/stock.service';
import { AuthService } from '../../services/auth.service';
import { catchError, forkJoin, of, Subject, take, takeUntil } from 'rxjs';
import {COMMON_IMPORTS} from "../../shared/common-imports";

@Component({
    selector: 'app-build-to-order',
    imports: [
        BreadcrumbComponent,
        DsBoxModule,
        DsButtonModule,
        DsCheckboxModule,
        DsFormFieldModule,
        DsLabelModule,
        DsLoadingModule,
        DsPopoverModule,
        DsSelectModule,
        NgForOf,
        NgIf,
        ReactiveFormsModule,
        DsTabsModule,
        FormsModule,
        DsIconModule,
        DsModalModule,
        DsRadioGroupModule,
        DsMessageModule,
        NgClass,
        DsProgressCircleModule,
        COMMON_IMPORTS
    ],
    templateUrl: './build-to-order.component.html',
    styleUrl: './build-to-order.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BuildToOrderComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    brandOptions: { id: string; label: string }[] = [];
    versionOptions: Version[] = [];
    modelOptions: DealerModel[] = [];
    filteredModelOptions: { id: string; label: string }[] = [];
    filteredVersionOptions: { id: string; label: string }[] = [];
    filteredModelOptionsReq: { id: string; label: string }[] = [];

    loadingAreaConfig: DsLoadingAreaConfiguration = { id: 'bodyLoading' };
    rowsData: BtoRowData[] = [];

    filters = {
        brand: '',
        brandName: '',
        version: '',
        model: '',
    };

    criteriaData: CriteriaResponse = {
        plant: '',
        plantName: '',
        plantCode: '',
        dealerBrands: [],
        dealerModels: [],
        storageLocations: [],
        versions: [],
        errorMessage: '',
        demoOrderingOpen: false,
    };

    btoRequest: BtoRequest = {
        docIndicator: undefined,
        document: undefined,
        quoteId: undefined,
        dspOrder: undefined,
        docType: undefined,
        dealerCode: '',
        brand: '',
        vehType: '',
        models: undefined,
    };

    rtpUploadDownloadModalIsOpen = false;
    rtpUploadDownloadModalData = {
        modelDescription: '',
        modelCode: '',
        quoteId: '',
        customerName: '',
        action: '',
        popReceived: '',
        rtpReceived: '',
        docType: '',
    };

    selectedFile: File | null = null;
    base64FileContent: string | null = null;
    popDocFile: File | null = null;

    uploadConfirmationModalIsOpen = false;
    uploadConfirmationModalData = {
        title: '',
        message: '',
    };

    isRTPTabClicked = true;
    isOTPTabClicked = false;
    isRTPSignedTabClicked = false;
    seachClicked = false;

    constructor(
        private readonly criteriaStoreService: CriteriaStoreService,
        private readonly criteriaService: CriteriaService,
        private readonly btoService: BtoQuoteService,
        private readonly stockService: StockService,
        private readonly loadingAreaService: DsLoadingAreaService,
        private readonly toastService: DsToastService,
        private readonly authService: AuthService
    ) {}

    ngOnInit(): void {
        this.authService.isAuthenticated().pipe(take(1)).subscribe((isAuthenticated) => {
            if (!isAuthenticated) {
                window.location.href = this.authService.getAuthUrl();
                return;
            }
            this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
            this.populateDropdowns();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private populateDropdowns(): void {
        const criteriaDataStore = this.criteriaStoreService.getCriteriaData();
        if (criteriaDataStore) {
            criteriaDataStore.pipe(takeUntil(this.destroy$)).subscribe({
                next: (criteriaData) => {
                    if (criteriaData) {
                        this.setCriteriaData(criteriaData);
                    }
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                },
                error: () => {
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                },
            });
        } else {
            const dealerCode = localStorage.getItem('dealerCode') ?? '';
            this.criteriaService
                .postCriteriaSelection(dealerCode)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (criteriaData) => {
                        this.criteriaStoreService.setCriteriaData(criteriaData);
                        this.setCriteriaData(criteriaData);
                        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    },
                    error: () => {
                        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    },
                });
        }
    }

    private setCriteriaData(criteriaData: CriteriaResponse): void {
        this.criteriaData = criteriaData;
        this.brandOptions = criteriaData.dealerBrands.map((brand) => ({
            id: brand.brandCode,
            label: brand.brandDescription,
        }));
        this.versionOptions = criteriaData.versions;
        this.modelOptions = criteriaData.dealerModels;
    }

    onBrandChange(event: SelectListOption | SelectListOption[] | null): void {
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        if (event && !Array.isArray(event)) {
            this.filters.brand = event.id;
            this.filters.brandName = event.label;
            this.filteredVersionOptions = this.versionOptions
                .filter((version) => String(version.brand) === String(this.filters.brand))
                .map((value) => ({ id: value.version, label: value.version }));
            this.filteredModelOptions = this.modelOptions
                .filter((model) => String(model.brand) === String(this.filters.brand))
                .map((value) => ({
                    id: value.modelCode,
                    label: value.modelCode + ' - ' + value.profileModelDescription,
                }));
            this.filteredModelOptionsReq = this.filteredModelOptions;
            this.btoRequest.models = this.filteredModelOptions.map((value) => value.id);
            this.filters.version = '';
            this.filters.model = '';
        } else {
            this.resetFilters();
            console.error('Invalid event type or null event:', event);
        }
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
    }

    onVersionChange(event: SelectListOption | SelectListOption[] | null): void {
        if (event && !Array.isArray(event)) {
            this.filters.version = event.label;
            this.filteredModelOptions = this.modelOptions
                .filter((model) => model.version === this.filters.version)
                .map((model) => ({
                    id: model.modelCode,
                    label: model.modelCode + ' - ' + model.profileModelDescription,
                }));
            this.filteredModelOptionsReq = this.filteredModelOptions;
            this.btoRequest.models = this.filteredModelOptions.map((value) => value.id);
            this.filters.model = '';
        } else {
            this.filters.version = '';
            this.filters.model = '';
            if (this.filters.brand) {
                this.filteredVersionOptions = this.versionOptions
                    .filter((version) => String(version.brand) === String(this.filters.brand))
                    .map((value) => ({ id: value.version, label: value.version }));

                this.filteredModelOptions = this.modelOptions
                    .filter((model) => String(model.brand) === String(this.filters.brand))
                    .map((value) => ({
                        id: value.modelCode,
                        label: value.modelCode + ' - ' + value.profileModelDescription,
                    }));

                this.filteredModelOptionsReq = this.filteredModelOptions;
                this.btoRequest.models = this.filteredModelOptions.map((value) => value.id);
            } else {
                this.resetFilters();
            }
            console.error('Invalid event type or null event:', event);
        }
    }

    onModelChange(event: SelectListOption | SelectListOption[] | null): void {
        if (event && !Array.isArray(event)) {
            this.filters.model = event.id;
            this.btoRequest.models = [event.id];
        } else {
            this.filters.model = '';
            this.btoRequest.models = this.filteredModelOptions.map((value) => value.id);
        }
    }

    onRTPTabClicked(): void {
        this.setActiveTab('rtp');
        this.getQuotes('E0003');
    }

    onRTPSignedTabClicked(): void {
        this.setActiveTab('rtpSigned');
        this.getQuotes('E0004');
    }

    onOTPLinkedTabClicked(): void {
        this.setActiveTab('otpLinked');
        this.getQuotes('E0005');
    }

    private setActiveTab(tabName: string): void {
        this.isRTPTabClicked = tabName === 'rtp';
        this.isRTPSignedTabClicked = tabName === 'rtpSigned';
        this.isOTPTabClicked = tabName === 'otpLinked';
        this.seachClicked = true;
    }

    getQuotes(status: string): void {
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        this.btoRequest.brand = this.filters.brand;
        this.btoRequest.dealerCode = localStorage.getItem('dealerCode') || '';
        this.btoRequest.vehType = this.filters.brandName.includes('MOTOR') ? '0' : '1';
        this.rowsData = [];

        this.btoService
            .getBtoItems(this.btoRequest)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.rowsData = (response.btoQuoteList || [])
                        .filter((quote) => quote.quoteStatus === status)
                        .map((quote) => this.mapToRowData(quote));

                    if (this.rowsData.length === 0) {
                        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                        return;
                    }

                    const imageRequests = this.rowsData.map((row) => {
                        const cosyRequest = { BODY_NO: row['bodyNo'] };
                        return this.stockService.getTableCosyUrl(cosyRequest).pipe(
                            catchError((err) => {
                                console.error(
                                    `Error fetching COSY URL for BODY_NO ${row['bodyNo']}`,
                                    err
                                );
                                return of({ exturl: '' });
                            })
                        );
                    });

                    forkJoin(imageRequests).subscribe((results) => {
                        results.forEach((res, idx) => {
                            this.rowsData[idx]['image'] = res.exturl || '';
                        });
                        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    });
                },
                error: () => {
                    this.rowsData = [];
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                },
            });
    }

    private mapToRowData(quote: BtoQuote): BtoRowData {
        return {
            image: '',
            bodyNo: quote.body,
            modelCode: quote.modelCode,
            description: quote.modelDescription,
            quoteId: quote.quoteId,
            quoteVersion: quote.quoteVersion,
            quoteDate: quote.quoteDate,
            customer: `${quote.customerSalutation} ${quote.customerInitials} ${quote.customerName} ${quote.customerSurname}`
                .replace(/\s+/g, ' ')
                .trim(),
            popReceived: quote.popAttached ? 'Yes' : 'No',
            rtpReceived: quote.rtpAttached ? 'Yes' : 'No',
            robot: quote.robot,
        };
    }

    openRTPUploadDownloadModal(
        quoteId: string,
        modelCode: string,
        modelDescription: string,
        customer: string,
        action: string,
        popReceived: string,
        rtpReceived: string
    ): void {
        this.rtpUploadDownloadModalData = {
            modelDescription,
            modelCode,
            quoteId,
            customerName: customer,
            action,
            popReceived,
            rtpReceived,
            docType: '',
        };
        this.rtpUploadDownloadModalIsOpen = true;
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            const reader = new FileReader();
            reader.onload = () => {
                this.base64FileContent = (reader.result as string).split(',')[1];
            };
            reader.readAsDataURL(this.selectedFile);
        }
    }

    onDocTypeChange(event: Event): void {
        const selectedValue = (event.target as HTMLInputElement).value;
        this.rtpUploadDownloadModalData.docType = selectedValue;
        console.log('Selected Document Type:', selectedValue);
    }

    downloadRTPDocuments(): void {
        if (!this.rtpUploadDownloadModalData.docType) {
            this.addToast('critical', 'Please select document type.');
            return;
        }
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        this.btoRequest.quoteId = this.rtpUploadDownloadModalData.quoteId;
        this.btoRequest.docIndicator = 'V';
        this.btoRequest.docType = this.rtpUploadDownloadModalData.docType;
        this.btoRequest.document = '';
        this.btoRequest.dealerCode = localStorage.getItem('dealerCode') || '';

        this.btoService
            .viewBtoDocument(this.btoRequest)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: BtoDownloadResponse) => {
                    if (response.doc) {
                        const decodedDoc = atob(response.doc.toString());
                        const byteArray = new Uint8Array(decodedDoc.length);
                        for (let i = 0; i < decodedDoc.length; i++) {
                            byteArray[i] = decodedDoc.charCodeAt(i);
                        }
                        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                        const blob = new Blob([byteArray], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        this.addToast('positive', 'Document downloaded successfully');
                        window.open(url, '_blank');
                    } else {
                        this.addToast('critical', 'Document not found or empty');
                        console.error('Document is null or undefined:', response.message);
                        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    }
                },
                error: () => {
                    this.addToast('critical', 'Document download failed');
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                },
            });
    }

    uploadRTPDocuments(): void {
        if (!this.rtpUploadDownloadModalData.docType) {
            this.addToast('critical', 'Please select document type.');
            return;
        }
        if (!this.base64FileContent) {
            this.addToast('critical', 'Please select a file to upload.');
            return;
        }
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        this.btoRequest.quoteId = this.rtpUploadDownloadModalData.quoteId;
        this.btoRequest.docIndicator = 'U';
        this.btoRequest.docType = this.rtpUploadDownloadModalData.docType;
        this.btoRequest.document = this.base64FileContent;
        this.btoRequest.dealerCode = localStorage.getItem('dealerCode') || '';

        this.btoService
            .uploadBtoDocument(this.btoRequest)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: BtoUploadResponse) => {
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    this.addToast('positive', response.message);
                    this.resetUploadState();
                    this.rtpUploadDownloadModalIsOpen = false;
                    this.onRTPSignedTabClicked();
                },
                error: () => {
                    this.addToast('critical', 'Document upload failed');
                    this.rtpUploadDownloadModalIsOpen = false;
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                },
            });
    }

    private resetUploadState(): void {
        this.selectedFile = null;
        this.base64FileContent = null;
        this.popDocFile = null;
    }

    close(): void {
        this.rtpUploadDownloadModalIsOpen = false;
        this.uploadConfirmationModalIsOpen = false;
        this.resetUploadState();
    }

    closeRTPModal(): void {
        this.popDocFile = null;
        const fileInput = document.getElementById(
            'rtpDocUpload'
        ) as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
        this.uploadConfirmationModalIsOpen = false;
    }

    get isBrandSelected(): boolean {
        return !!this.filters.brand;
    }

    searchBto(): void {
        this.loadingAreaConfig = {
            id: 'bodyLoading',
            loadingTitle: 'Loading...',
        };
        this.seachClicked = true;
        if (this.isRTPSignedTabClicked) this.onRTPSignedTabClicked();
        if (this.isRTPTabClicked) this.onRTPTabClicked();
        if (this.isOTPTabClicked) this.onOTPLinkedTabClicked();
    }

    isDocumentsAvailable(): boolean {
        return !(
            this.rtpUploadDownloadModalData.popReceived === 'Yes' ||
            this.rtpUploadDownloadModalData.rtpReceived === 'Yes'
        );
    }

    private addToast(
        tone: 'critical' | 'positive' | 'neutral',
        toastText: string
    ): void {
        this.toastService.pushToast({
            id: 'error-toast',
            tone,
            toastText,
        } as DsToastModel);
    }

    private resetFilters(): void {
        this.filters.brand = '';
        this.filters.version = '';
        this.filters.model = '';
        this.filteredVersionOptions = [];
        this.filteredModelOptions = [];
        this.filteredModelOptionsReq = [];
        this.btoRequest.models = [];
    }
}
